import axios from 'axios';
import { Dispatch } from 'redux';
import RECIPIENTS_TYPES, {
  RecipientProps,
  NonProfitProps,
  AccountsLinkProps,
  RecipientsRequestType,
} from './types';
import { ENV_CONFIG } from 'config/environment';
import { showToast } from 'components/Toast';
import API_ROUTES from 'services/apiRoutes';
import { getAccessToken } from 'services/storage';
import { buildQueryParams } from 'helpers/utils';
import { env } from 'config/environment/env_schema/env';
import type { RootState } from 'store/create-store';

const headers = {
  'Content-Type': 'application/json',
  'Access-Control-Allow-Origin': '*',
  'X-Auth-Version': 'v2',
};

export const getRecipients =
  ({
    search_str,
    recipient_ids,
    payment_processor,
    is_public_searchable,
    request_type = RecipientsRequestType.admin,
  }: RecipientProps) =>
  async (dispatch: Dispatch, getState: () => RootState): Promise<void> => {
    dispatch(RECIPIENTS_TYPES.recipientsStart());

    const activeRole = getState().admins?.activeRole ?? '';
    const params = buildQueryParams({
      search_str,
      payment_processor,
      recipient_ids,
      is_public_searchable,
      request_type,
      active_role: activeRole,
    });

    const url = `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.GET_RECIPIENTS(params)}`;

    try {
      const response = await axios.get(url, {
        headers: {
          ...headers,
          Authorization: `Bearer ${getAccessToken()}`,
          'X-API-Key': env.REACT_APP_API_KEY,
        },
      });
      dispatch(RECIPIENTS_TYPES.recipientsSuccess(response.data));
    } catch (error: any) {
      showToast(error.message, 'error', error?.response?.status);
      dispatch(RECIPIENTS_TYPES.recipientsError(error.message));
    }
  };

export const createRecipient =
  (body: NonProfitProps, activeRole: string, confirmDuplicate?: boolean) =>
  async (dispatch: Dispatch): Promise<any> => {
    dispatch(RECIPIENTS_TYPES.recipientsStart());

    const url = `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.CREATE_NON_PROFIT(activeRole, confirmDuplicate)}`;
    try {
      const response = await axios.post(url, body, {
        headers: { ...headers, Authorization: `Bearer ${getAccessToken()}` },
      });
      dispatch(RECIPIENTS_TYPES.nonProfitSuccess(response.data));
      return Promise.resolve(response);
    } catch (error: any) {
      // 409 conflicts are surfaced via the duplicate warning modal, not a generic toast
      if (error?.response?.status !== 409) {
        showToast(error.message, 'error', error?.response?.status);
      }
      dispatch(RECIPIENTS_TYPES.nonProfitError(error.message));
      return Promise.reject(error);
    }
  };

export const clearRecipients =
  () =>
  async (dispatch: Dispatch): Promise<void> => {
    dispatch(RECIPIENTS_TYPES.recipientsCleanUp());
  };

export const postAccountsLink = async (body: AccountsLinkProps, active_role: string) => {
  const url = `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.ACCOUNTS_LINK}?active_role=${active_role}`;
  try {
    const response = await axios.post(url, body, {
      headers: {
        ...headers,
        Authorization: `Bearer ${getAccessToken()}`,
        'X-API-Key': env.REACT_APP_API_KEY,
      },
    });
    return Promise.resolve(response.data);
  } catch (error: any) {
    showToast(error.message, 'error', error?.response?.status);
    return Promise.reject(error);
  }
};
